import { useQuery } from '@tanstack/react-query';

import { useUniversalNavContext } from './useUniversalNavContext';

import type { ZapierAccount } from '../types';

export const prepareAccountData = (json: any): ZapierAccount => {
  const account = json;
  const plan = account.plan_info;

  const output = {
    id: account.id,
    name: account.name,
    currency: account.currency,
    plan: {
      amount: plan.amount,
      hasMeteredBilling: plan.metered_task_pricing,
      id: plan.id,
      name: plan.name,
      interval: plan.interval,
    },
    isTrialing: account.is_trialing,
    trialEndDate: account.trial_end_date,

    isTrialingTeams: account.is_trialing_teams,
    teamTrialEndDate: account.teams_trial_end_date,
    teamsTrialAutoUpgrade: account.teams_trial_auto_upgrade,

    isTrialingCompanies: account.is_trialing_companies,
    companiesTrialEndDate: account.companies_trial_end_date,
    companiesTrialAutoUpgrade: account.companies_trial_auto_upgrade,

    isTeamAccount: account.is_team_account,
    teamAccountEndDate: account.team_account_end_date,

    isCompanyAccount: account.is_company_account,
    isUnlimited: account.is_unlimited,

    firstKnownUpgradeDate: account.first_known_upgrade_date,
    hasOverdueInvoice: account.has_overdue_invoice,
  } as ZapierAccount;

  return output;
};

export const useQueryAccount = (currentAccountId?: number) => {
  const { accountData: accountDataFromProps, apiHostname } =
    useUniversalNavContext();

  const result = useQuery<ZapierAccount>({
    queryKey: ['account', currentAccountId],
    queryFn: async () => {
      if (!currentAccountId) throw new Error('No currentAccountId specified.');
      const response = await fetch(
        `${apiHostname}/api/v4/accounts/${encodeURIComponent(
          currentAccountId
        )}`,
        {
          credentials:
            process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const json = await response.json();
      return json;
    },
    select: prepareAccountData,
    enabled: !accountDataFromProps && !!currentAccountId,
    initialData: accountDataFromProps,
  });

  return {
    ...result,
    // isInitialLoading returns false if the query is disabled.
    // isLoading returns true, which is undesired behavior.
    isLoading: result.isInitialLoading,
  } as typeof result;
};
